import { motion } from "framer-motion";
import { Zap, Briefcase, Handshake, Calculator, BrainCircuit } from "lucide-react";

const LAYERS = [
  {
    n: "1",
    tag: "BIOLOGY",
    name: "REFLEXES",
    icon: Zap,
    model: false,
    top: "#1A361A",
    side: "left",
    desc: "Sleep at bedtime or exhaustion: instant biology, resolved before anything else.",
    short: ["sleep at bedtime or exhaustion: instant", "biology, resolved before anything else."],
  },
  {
    n: "2",
    tag: "DUTY",
    name: "OBLIGATIONS",
    icon: Briefcase,
    model: false,
    top: "#306230",
    side: "right",
    desc: "Shifts, school, walking the kids, caring for someone ill. Duties come first, and everyone knows it.",
    short: ["shifts, school, walking the kids, caring", "for someone ill. duties come first."],
  },
  {
    n: "3",
    tag: "PROMISE",
    name: "COMMITMENTS",
    icon: Handshake,
    model: false,
    top: "#8BAC0F",
    side: "left",
    desc: "A promise due right now is a choice, not a command: it can be broken, and the town records exactly why.",
    short: ["a promise due now is a choice: keep it or", "break it, and the town records why."],
  },
  {
    n: "4",
    tag: "MATH",
    name: "UTILITY POLICY",
    icon: Calculator,
    model: false,
    top: "#9BBC0F",
    side: "right",
    desc: "The kernel lists every legal option and scores them on needs, traits, money, weather and beliefs, then rolls a fair dice.",
    short: ["every legal option scored on needs, traits,", "cash, weather and beliefs; then a fair dice."],
  },
  {
    n: "5",
    tag: "THE LLM",
    name: "DELIBERATION",
    icon: BrainCircuit,
    model: true,
    top: "#B8F0B8",
    side: "left",
    desc: "At real crossroads, a language model reads the resident's memories and proposes a move, with reasons. The kernel never blindly obeys: it samples, audits every decision, and benches a model that misbehaves.",
    short: ["a language model proposes a move with cited", "reasons; the kernel samples, audits, refuses."],
  },
];

const WORKFLOW = [
  { n: "01", t: "DECIDE & RECORD", d: "every change is a numbered decision" },
  { n: "02", t: "BRANCH & GATE", d: "tests must pass before merging" },
  { n: "03", t: "TEST", d: "runs, replays and live checks" },
  { n: "04", t: "DEPLOY", d: "a new town or a clean update" },
  { n: "05", t: "RESEARCH", d: "same seed, one change, measured" },
];

/* isometric plate geometry */
const CX = 450, W = 150, H = 75, T = 14, STEP = 64;

function platePts(cy) {
  return {
    top: `${CX},${cy - H} ${CX + W},${cy} ${CX},${cy + H} ${CX - W},${cy}`,
    left: `${CX - W},${cy} ${CX},${cy + H} ${CX},${cy + H + T} ${CX - W},${cy + T}`,
    right: `${CX + W},${cy} ${CX},${cy + H} ${CX},${cy + H + T} ${CX + W},${cy + T}`,
  };
}

export default function Method() {
  const plates = [...LAYERS].reverse(); // L5 top plate drawn first, lower plates after

  return (
    <section id="method" className="py-24 sm:py-32 bg-gb-bg2 border-y-2 border-gb-line">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">05 · THE METHOD</p>
          <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
            How a citizen decides
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-gb-light/90">
            Five layers, checked in order at every choice point. Only the top
            one has a language model inside, and even there the town holds
            the dice.
          </p>
        </motion.div>

        {/* isometric layer stack (desktop) */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9 }}
          className="hidden lg:block relative w-[900px] h-[540px] mx-auto"
          data-testid="decision-stack-diagram"
        >
          <svg viewBox="0 0 900 540" className="absolute inset-0 w-full h-full" role="img" aria-label="Isometric stack of the five decision layers">
            <ellipse cx={CX} cy={472} rx={210} ry={24} fill="#000" opacity="0.4" />
            {plates.map((l, i) => {
              const cy = 110 + i * STEP;
              const pts = platePts(cy);
              const isLeft = l.side === "left";
              return (
                <g key={l.n}>
                  <line
                    x1={isLeft ? 250 : 650}
                    y1={cy}
                    x2={isLeft ? CX - W : CX + W}
                    y2={cy}
                    stroke="#8BAC0F"
                    strokeOpacity="0.55"
                    strokeDasharray="3 3"
                  />
                  <polygon points={pts.left} fill="#0B160B" stroke="rgba(139,172,15,0.3)" strokeWidth="1" />
                  <polygon points={pts.right} fill="#0B160B" stroke="rgba(139,172,15,0.3)" strokeWidth="1" />
                  <polygon
                    points={pts.top}
                    fill={l.top}
                    stroke={l.model ? "#40F070" : "rgba(64,240,112,0.35)"}
                    strokeWidth={l.model ? 2 : 1}
                  />
                  {l.model && (
                    <polygon points={pts.top} fill="none" stroke="#40F070" strokeWidth="4" opacity="0.25" />
                  )}
                </g>
              );
            })}
            <text
              x={CX}
              y={524}
              textAnchor="middle"
              fontFamily="'JetBrains Mono', monospace"
              fontSize="11"
              letterSpacing="3"
              fill="#4A7C59"
            >
              FIVE LAYERS · CHECKED IN ORDER AT EVERY CHOICE POINT
            </text>
          </svg>

          {plates.map((l, i) => {
            const cy = 110 + i * STEP;
            const isLeft = l.side === "left";
            return (
              <div
                key={l.n}
                data-testid={`decision-layer-label-${l.n}`}
                className={`absolute w-[230px] ${isLeft ? "text-right" : "text-left"}`}
                style={
                  isLeft
                    ? { right: 655, top: cy - 36 }
                    : { left: 655, top: cy - 36 }
                }
              >
                <p className={`font-title text-[26px] leading-none ${l.model ? "text-gb-neon" : "text-gb-pale"}`}>
                  L{l.n} · {l.name}
                </p>
                <p className="mt-1.5 text-[10px] tracking-[0.2em] text-gb-accent">{l.tag}</p>
                {l.short.map((line) => (
                  <p key={line} className="text-[11px] leading-[16px] text-gb-light/80 whitespace-nowrap">
                    {line}
                  </p>
                ))}
              </div>
            );
          })}
        </motion.div>

        {/* stacked cards (mobile) */}
        <div className="lg:hidden max-w-5xl space-y-4">
          {LAYERS.map((l, i) => {
            const Icon = l.icon;
            return (
              <motion.div
                key={l.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: i * 0.06, duration: 0.55 }}
                className={`relative border-2 p-5 flex flex-col sm:flex-row sm:items-center gap-4 transition-colors duration-300 ${
                  l.model
                    ? "border-gb-neon/70 bg-gb-card shadow-[8px_8px_0_#1A361A,16px_16px_0_#0F1E0F]"
                    : "border-gb-line bg-gb-bg hover:border-gb-accent/50 shadow-[8px_8px_0_#0F1E0F]"
                }`}
                data-testid={`decision-stack-layer-${i + 1}`}
              >
                <span
                  className={`shrink-0 w-12 h-12 border-2 flex items-center justify-center font-pixel text-2xl ${
                    l.model ? "border-gb-neon/70 text-gb-neon" : "border-gb-accent/50 text-gb-accent"
                  }`}
                >
                  {l.n}
                </span>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon size={15} className={l.model ? "text-gb-neon" : "text-gb-accent"} />
                    <h3 className="font-title text-xl sm:text-2xl text-gb-pale tracking-wide">
                      {l.name}
                    </h3>
                    {l.model && (
                      <span className="font-arcade text-[6px] text-gb-bg bg-gb-neon px-2 py-1">
                        LLM
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-[12px] sm:text-[13px] leading-relaxed text-gb-light/85">
                    {l.desc}
                  </p>
                </div>
                <span
                  className={`self-start sm:self-center shrink-0 text-[9px] tracking-[0.25em] px-2.5 py-1 border ${
                    l.model ? "border-gb-neon/60 text-gb-neon" : "border-gb-accent/40 text-gb-accent"
                  }`}
                >
                  {l.tag}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* workflow grid */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mt-12 max-w-5xl mx-auto border-2 border-gb-line bg-gb-bg p-6 sm:p-7"
          data-testid="workflow-panel"
        >
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">HOW IT'S MADE</h3>
            <span className="text-[9px] tracking-[0.25em] text-gb-accent">
              EVERY STEP IS RECORDED
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {WORKFLOW.map((w, i) => (
              <div key={w.n} className="relative" data-testid={`workflow-step-${w.n}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-pixel text-2xl text-gb-neon leading-none">{w.n}</span>
                  <span className="hidden lg:block flex-1 h-px bg-gb-line" />
                </div>
                <p className="text-[11px] font-bold tracking-[0.15em] text-gb-pale">{w.t}</p>
                <p className="mt-1 text-[10px] leading-snug text-gb-muted">{w.d}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
